import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { profanityError } from '@h2h/shared';
import { useQueryClient } from '@tanstack/react-query';
import { AppShell, Content } from '../components/ui/AppShell';
import { AvatarUpload } from '../components/ui/AvatarUpload';
import { TextField } from '../components/auth/parts';
import { Check, ChevronLeft } from '../components/ui/icons';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useQueries';
import { AVATAR_TONES } from '../lib/avatar';
import { deleteAvatarObject } from '../lib/uploadAvatar';
import { cn } from '../lib/cn';

/** Edit the player's display name and avatar colour. Username is fixed. */
export default function EditProfile() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { uid, updateUserProfile } = useAuth();
  const { data: profile } = useProfile(uid);

  const [name, setName] = useState('');
  const [tone, setTone] = useState(0);
  const [photoURL, setPhotoURL] = useState<string | null>(null);
  const [seeded, setSeeded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!seeded && profile) {
    setName(profile.displayName ?? '');
    setTone(profile.avatarTone ?? 0);
    setPhotoURL(profile.photoURL ?? null);
    setSeeded(true);
  }

  // The photo persists on its own (a separate, immediate write) so it shows
  // everywhere right away — independent of the name/colour "Save changes" button.
  async function onPhotoUploaded(url: string) {
    setPhotoURL(url);
    setError(null);
    try {
      await updateUserProfile({ photoURL: url });
      await qc.invalidateQueries({ queryKey: ['user', uid] });
    } catch {
      setError('Could not save your photo. Please try again.');
    }
  }

  async function onPhotoRemove() {
    setPhotoURL(null);
    setError(null);
    try {
      await updateUserProfile({ photoURL: null });
      if (uid) await deleteAvatarObject(uid);
      await qc.invalidateQueries({ queryKey: ['user', uid] });
    } catch {
      setError('Could not remove your photo. Please try again.');
    }
  }

  const trimmed = name.trim();
  const dirty = trimmed !== (profile?.displayName ?? '').trim() || tone !== (profile?.avatarTone ?? 0);
  const canSave = trimmed.length >= 2 && dirty && !busy;

  async function onSave() {
    if (!canSave) return;
    const nameProfanity = profanityError(trimmed, 'name');
    if (nameProfanity) {
      setError(nameProfanity);
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await updateUserProfile({ displayName: trimmed, avatarTone: tone });
      await qc.invalidateQueries({ queryKey: ['user', uid] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      // Surface the server's verdict (e.g. the name filter) when it sent one.
      const code = (err as { code?: string })?.code ?? '';
      const message = (err as { message?: string })?.message;
      setError(
        code.includes('invalid-argument') && message
          ? message
          : 'Could not save your changes. Please try again.'
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell>
      <Content>
        <div className="contents md:mx-auto md:block md:w-full md:max-w-xl">
        <div className="flex items-center justify-between pt-2 md:pt-5">
          <button
            type="button"
            onClick={() => navigate('/settings')}
            className="tap -ml-1 flex items-center gap-[2px] text-ink"
          >
            <ChevronLeft width={22} height={22} strokeWidth={2.4} />
            <span className="text-[16px] font-semibold tracking-snug">Settings</span>
          </button>
        </div>

        <div className="pt-3">
          <h1 className="title text-[30px] md:text-[40px]">Edit profile</h1>
        </div>

        <div className="mt-6 rounded-2xl border border-black/5 bg-card p-4 shadow-tile">
          <div className="flex flex-col items-center">
            <AvatarUpload
              uid={uid}
              name={trimmed}
              tone={tone}
              src={photoURL}
              size={84}
              onUploaded={onPhotoUploaded}
              onRemove={onPhotoRemove}
            />
            <div className="mt-4 flex flex-wrap items-center justify-center gap-[10px]">
              {AVATAR_TONES.map((t, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setTone(i)}
                  style={{ backgroundImage: `linear-gradient(135deg, ${t.from}, ${t.to})` }}
                  className={cn(
                    'h-7 w-7 rounded-full transition-transform active:scale-90',
                    i === tone ? 'ring-2 ring-ink ring-offset-2 ring-offset-card' : ''
                  )}
                  aria-label={`Avatar colour ${i + 1}`}
                />
              ))}
            </div>
          </div>

          <div className="mt-5">
            <TextField
              label="Display name"
              value={name}
              onChange={setName}
              placeholder="Your name"
              autoComplete="name"
              maxLength={30}
            />
          </div>

          {profile?.username && (
            <p className="mt-3 text-[12px] font-medium text-ink3">
              Username @{profile.username} can’t be changed.
            </p>
          )}

          {error && (
            <p className="mt-3 rounded-xl bg-miss/10 px-3 py-2 text-[13px] font-semibold text-danger">{error}</p>
          )}

          <button
            type="button"
            onClick={onSave}
            disabled={!canSave}
            className={cn(
              'tap mt-4 flex w-full items-center justify-center gap-2 rounded-full py-[14px] text-[15px] font-extrabold tracking-snug text-white transition-colors disabled:opacity-40',
              saved ? 'bg-gradient-to-br from-accent to-accent-deep shadow-featured' : 'bg-night shadow-dark'
            )}
          >
            {saved ? (
              <>
                <Check width={17} height={17} strokeWidth={3} />
                Saved
              </>
            ) : busy ? (
              'Saving…'
            ) : (
              'Save changes'
            )}
          </button>
        </div>
        </div>
      </Content>
    </AppShell>
  );
}
